import type { HostSanitizationState } from '@playcastdotio/CoreTypes';
import type { MatchmakeableBlockReason } from '@playcastdotio/Messaging';

export type SanitizationHealth = {
  isBetaHost: boolean;
  sanitized: boolean;
  failureCode: string;
  failureDetail: string;
  hostAudioHideFailureReason?: string;
  blockReason: MatchmakeableBlockReason | null;
  // Observe-only telemetry. `undefined` means the replicated state did not carry
  // these fields (e.g. the store was cleared to `{}` for an in-flight refresh).
  // Consumers MUST treat `undefined` as "unknown / preserve last known" rather
  // than coercing to 0, otherwise a refresh would prematurely report 0 violations.
  // A genuine reply always carries them (C++ ToJson always serializes the count).
  processPolicyViolationCount?: number;
  lastBlockedProcessName?: string;
  lastBlockedReason?: string;
};

export type SanitizationHealthListener = (health: SanitizationHealth) => void;

export interface SanitizationHealthSource {
  getHealth(): SanitizationHealth;
  onHealthChange(listener: SanitizationHealthListener): () => void;
}

const FAILURE_CODE_TO_BLOCK_REASON: Record<string, MatchmakeableBlockReason> = {
  process_policy: 'process_policy_violation',
  virtual_display: 'virtual_display_unhealthy',
  physical_isolation: 'physical_isolation_unhealthy',
  host_audio: 'host_audio_unhealthy',
  multiple: 'sanitization_failure',
};

export function mapSanitizationFailureToBlockReason(failureCode: string): MatchmakeableBlockReason | null {
  if (!failureCode || failureCode === 'none') return null;
  return FAILURE_CODE_TO_BLOCK_REASON[failureCode] ?? 'sanitization_failure';
}

export type SanitizationHostConfigFields = {
  sanitizationFailureCode?: string;
  sanitizationFailureDetail?: string;
  hostAudioHideFailureReason?: string;
};

/** Maps replicated sanitization health into HOST_CONFIG diagnostic fields for fleet/admin. */
export function resolveSanitizationHostConfigFields(
  health: SanitizationHealth | undefined,
  current: SanitizationHostConfigFields,
): SanitizationHostConfigFields {
  // Unsanitized beta host: surface the granular failure diagnostics for the fleet cell.
  if (health?.isBetaHost === true && health.sanitized === false) {
    return {
      sanitizationFailureCode: health.failureCode !== 'none' ? health.failureCode : undefined,
      sanitizationFailureDetail: health.failureDetail || undefined,
      hostAudioHideFailureReason:
        health.hostAudioHideFailureReason && health.hostAudioHideFailureReason !== 'none'
          ? health.hostAudioHideFailureReason
          : undefined,
    };
  }

  // Any other *definitive* verdict is not blocked, so clear stale diagnostics — a beta host
  // that became sanitized, OR a non-beta host (including the post-teardown idle state). The
  // non-beta case previously fell through to the preserve branch below, leaving a prior
  // block's failure code/detail rendering on the matchmakeable cell after the host was no
  // longer blocked. (The monitor holds a truly-blocked beta host closed across a `{}` refresh
  // via lastConfirmedHealth, so reaching here always means genuinely not-blocked.)
  if (health !== undefined) {
    return {
      sanitizationFailureCode: undefined,
      sanitizationFailureDetail: undefined,
      hostAudioHideFailureReason: undefined,
    };
  }

  // No health available (source not yet bound): we have no basis to update, so preserve
  // whatever HOST_CONFIG already carries rather than fabricating a change.
  return {
    sanitizationFailureCode: current.sanitizationFailureCode,
    sanitizationFailureDetail: current.sanitizationFailureDetail,
    hostAudioHideFailureReason: current.hostAudioHideFailureReason,
  };
}

type SanitizationStateLike = Partial<HostSanitizationState> | undefined;
type GetSanitizationStateFn = () => SanitizationStateLike;

export class NonsoleSanitizationMonitor implements SanitizationHealthSource {
  private readonly listeners = new Set<SanitizationHealthListener>();
  private lastNotifiedHealth: SanitizationHealth | null = null;
  // Last health derived from a *definitive* payload (one that carried an explicit
  // `isBetaHost` classification). Used to hold a confirmed beta block closed across a
  // transient `{}` refresh clear so the host does not briefly flip to matchmakeable.
  private lastConfirmedHealth: SanitizationHealth | null = null;

  constructor(private readonly getSanitizationState: GetSanitizationStateFn) {}

  public getHealth(): SanitizationHealth {
    return this.computeHealth();
  }

  public onHealthChange(listener: SanitizationHealthListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  public notifyIfChanged(): void {
    const health = this.computeHealth();
    if (this.healthEquals(this.lastNotifiedHealth, health)) return;
    this.lastNotifiedHealth = health;
    for (const listener of this.listeners) {
      try {
        listener(health);
      } catch (err) {
        console.error('[NonsoleSanitizationMonitor] health listener threw', err);
      }
    }
  }

  private computeHealth(): SanitizationHealth {
    const state = this.getSanitizationState();

    // A genuine replicated payload always carries an explicit `isBetaHost` value
    // (C++ ToJson always serializes it). Its absence means the store was cleared to
    // `{}` for an in-flight refresh (or nothing has arrived yet) — this is NOT a real
    // "non-beta" classification and must not be allowed to open the gate.
    const hasDefinitiveClassification = state !== undefined && state.isBetaHost !== undefined;

    if (!hasDefinitiveClassification) {
      // Refresh clear: if we previously confirmed a beta host that was blocked, keep
      // that gate closed until a full healthy / non-beta payload arrives. This
      // distinguishes a transient `{}` refresh from the initial empty state (no
      // confirmed history), which safely defaults to sanitized so a brand-new or
      // non-beta host is never wrongly blocked while the first full payload is in flight.
      if (this.lastConfirmedHealth?.isBetaHost && !this.lastConfirmedHealth.sanitized) {
        return this.lastConfirmedHealth;
      }
      return this.computeHealthFromState(state);
    }

    // Definitive payload: recompute and remember it so a subsequent `{}` clear can
    // preserve the confirmed gate (blocked) or correctly release it (healthy/non-beta).
    const health = this.computeHealthFromState(state);
    this.lastConfirmedHealth = health;
    return health;
  }

  private computeHealthFromState(state: SanitizationStateLike): SanitizationHealth {
    const isBetaHost = state?.isBetaHost === true;
    // Fail-closed for a host the payload identifies as beta: it counts as sanitized
    // ONLY when the replicated state explicitly says so. A partial payload (e.g. an
    // `isBetaHost: true` snapshot that has not yet carried a `sanitized` value)
    // must NOT open the gate. Non-beta / not-yet-classified hosts keep the safe
    // default (treated sanitized) so they are never wrongly blocked while the first
    // full payload is in flight.
    const sanitized = isBetaHost ? state?.sanitized === true : state?.sanitized !== false;
    const failureCode = state?.failureCode ?? 'none';
    const failureDetail = state?.failureDetail ?? '';
    const hostAudioHideFailureReason = state?.hostAudioHideFailureReason;
    const blockReason =
      isBetaHost && !sanitized ? mapSanitizationFailureToBlockReason(failureCode) : null;

    return {
      isBetaHost,
      sanitized,
      failureCode,
      failureDetail,
      hostAudioHideFailureReason,
      blockReason,
      // Leave undefined (not 0/'') when absent so a cleared refresh state does not
      // masquerade as "zero violations" and prematurely reset the persisted count.
      processPolicyViolationCount: state?.processPolicyViolationCount,
      lastBlockedProcessName: state?.lastBlockedProcessName,
      lastBlockedReason: state?.lastBlockedReason,
    };
  }

  private healthEquals(a: SanitizationHealth | null, b: SanitizationHealth): boolean {
    if (!a) return false;
    return (
      a.isBetaHost === b.isBetaHost &&
      a.sanitized === b.sanitized &&
      a.failureCode === b.failureCode &&
      a.failureDetail === b.failureDetail &&
      a.hostAudioHideFailureReason === b.hostAudioHideFailureReason &&
      a.blockReason === b.blockReason &&
      a.processPolicyViolationCount === b.processPolicyViolationCount &&
      a.lastBlockedProcessName === b.lastBlockedProcessName &&
      a.lastBlockedReason === b.lastBlockedReason
    );
  }
}
